var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useEffect, useContext, useMemo, useRef, useReducer, useCallback, } from 'react';
import { v4 as uuid4 } from 'uuid';
import Portal from '@reach/portal';
import { VisuallyHidden } from '../components/VisuallyHidden';
import { LiveRegion, } from '../components/LiveRegion';
import { regionReducer, initialRegionState, ACTIONS, } from '../utils/reducerUtils';
import { isValidAnnouncement } from '../utils';
var initialState = {
    announcePolite: function () {
        /* noop */
    },
    announceAssertive: function () {
        /* noop */
    },
};
export var AriaLiveContext = React.createContext(initialState);
export var AriaLiveConsumer = AriaLiveContext.Consumer;
var AriaLiveProvider = function (_a) {
    var children = _a.children;
    var landmarkId = useState("aria-live-regions-" + uuid4())[0];
    var sectionRef = useRef(null);
    var ariaLiveContext = useContext(AriaLiveContext);
    var outerProviderDetected = useMemo(function () {
        return (ariaLiveContext.announcePolite !== initialState.announcePolite &&
            ariaLiveContext.announceAssertive !== initialState.announceAssertive);
    }, [ariaLiveContext]);
    var _b = useReducer(regionReducer, initialRegionState), regionState = _b[0], dispatchRegionState = _b[1];
    var _c = useState(false), remountPortal = _c[0], setRemountPortal = _c[1];
    useEffect(function () {
        if (remountPortal)
            setRemountPortal(false);
    }, [remountPortal]);
    /**
     * We need to make sure that this element is visible to screen readers
     * and not modified by any third-party libraries. It is common for third-party
     * modals to attach an aria-hidden=true attribute, which will cause some
     * screen readers to ignore content that changes the second time we cycle
     * through the aria-live regions unless we re-mount the component.
     * (side note: tried using keys - doesn't work)
     */
    var clearAttributes = useCallback(function () {
        var _a;
        var parentElement = (_a = sectionRef.current) === null || _a === void 0 ? void 0 : _a.parentElement;
        if (parentElement && parentElement.getAttribute('aria-hidden')) {
            dispatchRegionState({ type: ACTIONS.RESET_STATE });
            setRemountPortal(true);
        }
    }, [sectionRef]);
    var queueAnnouncement = useCallback(function (announcement, setting, config) {
        if (isValidAnnouncement(announcement)) {
            clearAttributes();
            dispatchRegionState({
                type: ACTIONS.ENQUEUE,
                payload: __assign({ announcement: announcement,
                    setting: setting }, (config || {})),
            });
        }
    }, [clearAttributes, dispatchRegionState]);
    var announcePolite = useCallback(function (announcement, config) {
        queueAnnouncement(announcement, 'polite', config);
    }, [queueAnnouncement]);
    var announceAssertive = useCallback(function (announcement, config) {
        var _a;
        /**
         * The default role for LiveRegion's is log, which should not be paired with
         * assertive messages. We're forcing the default to alert here to match the
         * vast majority of use cases. More information on this can be found here:
         * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions#Preferring_specialized_live_region_roles
         */
        queueAnnouncement(announcement, 'assertive', __assign(__assign({}, config), { role: ((_a = config) === null || _a === void 0 ? void 0 : _a.role) || 'alert' }));
    }, [queueAnnouncement]);
    return (React.createElement(React.Fragment, null, outerProviderDetected ? (children) : (React.createElement(AriaLiveContext.Provider, { value: {
            announcePolite: announcePolite,
            announceAssertive: announceAssertive,
        } },
        children,
        !remountPortal && (React.createElement(Portal, { type: "use-aria-live-portal" },
            React.createElement(VisuallyHidden, { ref: sectionRef, id: landmarkId, as: "section", "aria-label": "You reached the end of the customer portion of this page. The following content is for ally\u2019s internal team only - please disregard if it doesn\u2019t apply." },
                React.createElement(VisuallyHidden, { "aria-describedby": landmarkId }),
                React.createElement(LiveRegion, __assign({}, regionState.queue[0], { "data-testid": "live-region-0" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[1], { "data-testid": "live-region-1" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[2], { "data-testid": "live-region-2" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[3], { "data-testid": "live-region-3" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[4], { "data-testid": "live-region-4" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[5], { "data-testid": "live-region-5" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[6], { "data-testid": "live-region-6" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[7], { "data-testid": "live-region-7" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[8], { "data-testid": "live-region-8" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[9], { "data-testid": "live-region-9" })))))))));
};
export { AriaLiveProvider };
//# sourceMappingURL=AriaLiveProvider.js.map